import { useFormikContext } from 'formik';
import { useCallback, useMemo, useState } from 'react';

import { type PartFileUnits } from '@frontend/api';
import {
  Button,
  CopyIcon,
  Divider,
  DragHandleIcon,
  Flex,
  Heading,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  TrashIcon,
  VStack,
} from '@frontend/components';

import { ItemAndDropBorderContainer } from 'components/BetweenItemDropBorder';
import { CUSTOM_DRAGGABLE_ITEM_TYPES } from 'components/customDragLayer/const';
import { useDrag } from 'components/customDragLayer/hooks/useDrag';

import { useReqsReviewConfigContext } from '../ReqsReviewConfigContext';
import {
  buildFilterGroupFromCascaderValue,
  type SignalWithId,
} from '../utils/queryBuilding';
import { BetweenGroupDropBorder } from './BetweenGroupDropBorder';
import { FilterGroupComponent } from './FilterGroupComponent';
import { FormStateData } from './RulesDetailModal';
import { SignalSelect } from './SignalSelect';

const GAP = 1.5;

type SignalComponentProps = {
  signal: SignalWithId;
  signalIndex: number;
  isReadOnly: boolean;
  signalUnits: PartFileUnits;
  onReorderGroups: (
    oldSignalIndex: number,
    oldGroupIndex: number,
    newSignalIndex: number,
    newGroupIndex: number,
  ) => void;
};

export const SignalComponent = ({
  signal,
  signalIndex,
  isReadOnly,
  signalUnits,
  onReorderGroups,
}: SignalComponentProps) => {
  const [isAddingGroup, setIsAddingGroup] = useState(false);
  const { values, setFieldValue } = useFormikContext<FormStateData>();
  const signals = values.signals;

  const { cascaderOptions, configMap, valueTypeToAvailableUnits } =
    useReqsReviewConfigContext();

  const handleAddGroup = useCallback(
    (fullPathConfigArray: string[]) => {
      // toggle back to showing add button
      setIsAddingGroup(false);

      if (!configMap) return;

      const newGroup = buildFilterGroupFromCascaderValue({
        nextCascaderValue: fullPathConfigArray,
        configMap,
        valueTypeToAvailableUnits,
      });

      if (!newGroup) {
        console.warn(
          `New group not found for cascader value ${fullPathConfigArray}`,
        );
        return;
      }

      const updatedSignal = { ...signal };
      updatedSignal.groups.push(newGroup);

      const newSignals = [...signals];
      newSignals[signalIndex] = updatedSignal;
      setFieldValue('signals', newSignals);
    },
    [
      configMap,
      valueTypeToAvailableUnits,
      setFieldValue,
      signal,
      signalIndex,
      signals,
    ],
  );

  return (
    <>
      <VStack spacing={GAP} align="stretch">
        {signal.groups.map((group, groupIndex) => (
          <ItemAndDropBorderContainer
            key={group.id}
            display="flex"
            flexDirection="column"
            gap={GAP}
          >
            <BetweenGroupDropBorder
              side="top"
              signalIndex={signalIndex}
              groupIndex={groupIndex}
              onReorderGroups={onReorderGroups}
              isFirstGroup={groupIndex === 0}
              isLastGroup={groupIndex === signal.groups.length - 1}
              logicalOperator={signal.logicalOperator}
            />
            <FilterGroupComponent
              signal={signal}
              group={group}
              groupIndex={groupIndex}
              signalIndex={signalIndex}
              isReadOnly={isReadOnly}
              unit={signalUnits}
            />
            <BetweenGroupDropBorder
              side="bottom"
              signalIndex={signalIndex}
              groupIndex={groupIndex + 1}
              onReorderGroups={onReorderGroups}
              isFirstGroup={groupIndex === 0}
              isLastGroup={groupIndex === signal.groups.length - 1}
              logicalOperator={signal.logicalOperator}
            />
          </ItemAndDropBorderContainer>
        ))}
      </VStack>
      {isAddingGroup || signal.groups.length === 0 ? (
        <ItemAndDropBorderContainer>
          {signal.groups.length === 0 && (
            <BetweenGroupDropBorder
              side="top"
              signalIndex={signalIndex}
              groupIndex={signal.groups.length}
              onReorderGroups={onReorderGroups}
              isFirstGroup={true}
              isLastGroup={false}
              logicalOperator={signal.logicalOperator}
              isPlaceholder={true}
            />
          )}
          <HStack alignItems="center">
            <IconButton size="md" isDisabled={true}>
              <DragHandleIcon />
            </IconButton>
            <SignalSelect
              isDisabled={isReadOnly}
              isClearable={false}
              placeholder="Add a signal"
              value={[]}
              options={cascaderOptions}
              onChange={handleAddGroup}
              className="signal-select-add-group"
            />
          </HStack>
        </ItemAndDropBorderContainer>
      ) : (
        <HStack alignItems="center">
          <IconButton size="md" isDisabled={true}>
            <DragHandleIcon />
          </IconButton>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setIsAddingGroup(true)}
            isDisabled={isReadOnly}
          >
            And +
          </Button>
        </HStack>
      )}
    </>
  );
};

type SignalComponentWithCaseWrapperProps = SignalComponentProps & {
  onDuplicate: (signalIndex: number) => void;
  onDeleteSignal: (signalIndex: number) => void;
  onReorderGroups: (
    oldSignalIndex: number,
    oldGroupIndex: number,
    newSignalIndex: number,
    newGroupIndex: number,
  ) => void;
};

export const SignalComponentWithCaseWrapper = ({
  signal,
  signalIndex,
  isReadOnly,
  signalUnits,
  onDuplicate,
  onDeleteSignal,
  onReorderGroups,
}: SignalComponentWithCaseWrapperProps) => {
  const handleDuplicate = useCallback(() => {
    onDuplicate(signalIndex);
  }, [onDuplicate, signalIndex]);

  const handleDelete = useCallback(() => {
    onDeleteSignal(signalIndex);
  }, [onDeleteSignal, signalIndex]);

  // Drag functionality for signals
  const dragItem = useMemo(
    () => ({
      draggableItemType: CUSTOM_DRAGGABLE_ITEM_TYPES.RULES_SIGNAL,
      description: `Case ${signalIndex + 1}`,
      signal: {
        signalId: signal.id,
        signalIndex,
      },
    }),
    [signal.id, signalIndex],
  );

  const [{ isDragging }, connectDragSource] = useDrag({
    item: dragItem,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <Flex
      direction="column"
      px={1}
      py={1.5}
      border="1px solid"
      borderColor="separator.border"
      borderRadius="md"
      gap={GAP}
      backgroundColor="page.surface"
      opacity={isDragging ? 0.5 : 1}
    >
      <HStack justify="space-between" align="center">
        <HStack spacing={2} align="center">
          {!isReadOnly && (
            <IconButton ref={connectDragSource} size="md" cursor="grab">
              <DragHandleIcon />
            </IconButton>
          )}
          <Heading size="md">Case {signalIndex + 1}</Heading>
        </HStack>
        <HStack spacing={2}>
          {!isReadOnly && (
            <Menu size="sm">
              <MenuButton>Actions</MenuButton>
              <Portal>
                <MenuList>
                  <MenuItem icon={<CopyIcon />} onClick={handleDuplicate}>
                    Duplicate
                  </MenuItem>
                  <MenuItem
                    icon={<TrashIcon />}
                    colorScheme="destructive"
                    onClick={handleDelete}
                  >
                    Delete
                  </MenuItem>
                </MenuList>
              </Portal>
            </Menu>
          )}
        </HStack>
      </HStack>
      <Divider />
      <SignalComponent
        signal={signal}
        signalIndex={signalIndex}
        isReadOnly={isReadOnly}
        signalUnits={signalUnits}
        onReorderGroups={onReorderGroups}
      />
    </Flex>
  );
};

export const SignalComponentSimple = ({
  signal,
  signalIndex,
  isReadOnly,
  signalUnits,
  onReorderGroups,
}: SignalComponentProps) => {
  return (
    <SignalComponent
      signal={signal}
      signalIndex={signalIndex}
      isReadOnly={isReadOnly}
      signalUnits={signalUnits}
      onReorderGroups={onReorderGroups}
    />
  );
};
